'use client';

import Link from 'next/link';
import Image from 'next/image';
import { UserIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { Article } from '../data/articles';

interface BlogArticleGridProps {
  articles: Article[];
}

export default function BlogArticleGrid({ articles }: BlogArticleGridProps) {
  return (
    <section className="bg-white py-10 sm:py-16 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {articles.length === 0 ? (
          <p className="text-center text-charcoal text-sm sm:text-base py-10 sm:py-12 px-4">
            No articles match your filters. Try a different category or search.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
            {articles.map((article) => (
              <article
                key={article.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col"
              >
                {/* Featured Image */}
                <div className="relative w-full h-40 sm:h-48 bg-gray-100">
                  <Image
                    src={article.image}
                    alt={article.title}
                    fill
                    className="object-cover"
                  />
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 flex-1 flex flex-col">
                  {/* Category Label */}
                  <span className="inline-block px-3 py-1 bg-gold text-white text-xs font-semibold rounded mb-2 sm:mb-3 self-start">
                    {article.category}
                  </span>

                  {/* Title */}
                  <h3 className="text-navy font-bold text-lg sm:text-xl mb-2 sm:mb-3 line-clamp-2 leading-tight">
                    {article.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-charcoal text-[13px] sm:text-sm leading-relaxed mb-3 sm:mb-4 flex-1">
                    {article.excerpt}
                  </p>

                  {/* Read More Link */}
                  <Link
                    href={`/blog/${article.slug}`}
                    className="text-gold font-medium text-sm hover:underline mb-3 sm:mb-4 inline-block min-h-[44px] flex items-center"
                  >
                    Read More →
                  </Link>

                  {/* Meta Information */}
                  <div className="flex items-center justify-between text-gray-500 text-[11px] sm:text-xs pt-3 sm:pt-4 border-t border-gray-100 gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span>{article.author}</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <span>{article.date}</span>
                      <div className="flex items-center gap-1">
                        <ClockIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
